// Product breakdown — active-point driver for the `product-breakdown` section.
//
// The section ships two layouts (chosen in CSS by viewport + reduced-motion; CSS-first, no JS-gated
// swap). This module only runs the INTERACTIVE behaviour, and only when the pinned desktop layout is
// active (≥750px AND motion allowed):
//   • an IntersectionObserver watches each point against a center line (rootMargin collapses the
//     viewport to a 1px band at 50% height); the point crossing that line becomes active,
//   • the active point lights its marker (`.is-active`), lifts its card out of the dimmed stack, and
//     crossfades in its close-up layer (if any), and announces itself on an aria-live status line.
// In the mobile / reduced-motion layout it does nothing — the points read as a numbered list.
//
// Marker click-to-scroll works in ALL layouts (the markers are real buttons): clicking a marker
// scrolls its point to center. Re-evaluates on resize / motion-preference change; reaps on section
// unload so its observer + listeners don't leak across theme-editor re-renders.

import { mountInstances } from '@theme/ca-section-instances';

(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductBreakdown {
    constructor(root) {
      this.root = root;
      this.points = Array.from(root.querySelectorAll('[data-bp-point]'));
      this.markers = Array.from(root.querySelectorAll('[data-bp-marker]'));
      this.status = root.querySelector('[data-pb-status]');

      // id → { point, marker, layer, index }
      this.byId = new Map();
      this.points.forEach((point, index) => {
        const id = point.dataset.bpId;
        this.byId.set(id, {
          point,
          index,
          marker: this.markers.find((m) => m.dataset.bpId === id) || null,
          layer: null,
        });
      });

      this.activeId = null;
      this.observer = null;
      this.enabled = false;
      this.visible = new Set(); // ids currently crossing the center line

      this.onIntersect = this.onIntersect.bind(this);
      this.reevaluate = this.reevaluate.bind(this);

      this.bindMarkers();
      this.apply();

      // Mode only flips when a breakpoint / motion preference is crossed — listen to those, not resize.
      this.listen(MOBILE, 'add');
      this.listen(REDUCE, 'add');
    }

    addLayers() {
      const media = this.root.querySelector('[data-pb-media]');
      if (!media) return;
      const markerWrap = media.querySelector(':scope > [data-pb-markers]');
      this.byId.forEach((entry, id) => {
        if (entry.layer) return;
        const template = entry.point.querySelector('[data-bp-layer-template]');
        if (!template) return;
        media.insertBefore(template.content.cloneNode(true), markerWrap);
        entry.layer = media.querySelector(`[data-bp-layer][data-bp-id="${id}"]`);
      });
    }

    listen(mql, op) {
      const method = op === 'add' ? 'addEventListener' : 'removeEventListener';
      const legacy = op === 'add' ? 'addListener' : 'removeListener';
      if (mql[method]) mql[method]('change', this.reevaluate);
      else if (mql[legacy]) mql[legacy](this.reevaluate); // older Safari
    }

    interactive() {
      return !MOBILE.matches && !REDUCE.matches;
    }

    // Clicking a marker scrolls its point to center. Active in every layout (the dots are buttons).
    bindMarkers() {
      this.markers.forEach((marker) => {
        marker.addEventListener('click', () => {
          const entry = this.byId.get(marker.dataset.bpId);
          if (!entry) return;
          const behavior = REDUCE.matches ? 'auto' : 'smooth';
          entry.point.scrollIntoView({ behavior, block: 'center' });
          const target = entry.point.querySelector('.product-breakdown__point-inner') || entry.point;
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      });
    }

    reevaluate() {
      this.apply();
    }

    apply() {
      const want = this.interactive();
      if (want === this.enabled) return;
      this.enabled = want;
      if (want) this.enable();
      else this.disable();
    }

    enable() {
      this.addLayers();
      // A 1px band at the vertical middle of the viewport: a point intersects only while its box
      // straddles the center line, so the active point tracks the one the visitor is reading.
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      });
      this.points.forEach((point) => this.observer.observe(point));
    }

    disable() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.visible.clear();
      this.setActive(null);
      if (this.status) this.status.textContent = '';
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        const id = entry.target.dataset.bpId;
        if (entry.isIntersecting) this.visible.add(id);
        else this.visible.delete(id);
      });
      if (!this.visible.size) return; // between points: keep the last active one lit
      // Choose the topmost still-crossing point (smallest index) for a stable, ordered result.
      let nextId = null;
      let best = Infinity;
      this.visible.forEach((id) => {
        const idx = this.byId.get(id)?.index ?? Infinity;
        if (idx < best) {
          best = idx;
          nextId = id;
        }
      });
      this.setActive(nextId);
    }

    setActive(id) {
      if (id === this.activeId) return;
      this.activeId = id;

      this.byId.forEach((entry, entryId) => {
        const on = entryId === id;
        entry.point.classList.toggle('is-active', on);
        if (entry.marker) entry.marker.classList.toggle('is-active', on);
        if (entry.layer) entry.layer.classList.toggle('is-active', on);
      });

      if (this.status && id != null) {
        const entry = this.byId.get(id);
        const label = entry.point.dataset.bpLabel;
        this.status.textContent = `Point ${entry.index + 1} of ${this.points.length}${label ? `: ${label}` : ''}`;
      }
    }

    destroy() {
      this.disable();
      this.listen(MOBILE, 'remove');
      this.listen(REDUCE, 'remove');
    }
  }

  mountInstances({
    selector: '.product-breakdown',
    create: (root) => new ProductBreakdown(root),
    prop: '__productBreakdown',
  });
})();
